import React, { useEffect, useState, useContext } from "react";
import CarrierDetailsForm from "./CarrierDetailsForm/CarrierDetailsForm";
import { CarrierDetail } from "../Model/CarrierDetail";
import CarrierManagementAPI from "../Services/CarrierManagementAPI";
import { Spinner } from "../../../components";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Button, Tab, Nav } from "react-bootstrap";
import { CarrierManagementConstants } from '../../../shared/util/constant';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as label from '../../../shared/util/label';
import './CarrierDetailsPanel.scss';
import CarrierCommissionGrid from '../CarrierCommission/CarrierCommissionGrid';
import AppContext from '../../../shared/components/AppContext';
import { useConfirmationModalContext } from '../../../shared/components/ModalConfirmationContext';
import * as constant from '../../../shared/util/constant';
import RetentionArrayGrid from '../RetentionArray/RetentionArrayGrid';

interface Props {
  carrierId?: string;
  parentCompanyId?: string;
  parentDetail?: any;
  mode: 'none' | 'new' | 'view' | 'edit';
  onRefreshLeftPanel?: () => void;
  onCarrierCreated?: (id: string, created?: any) => void;
  onAddNew?: () => void;
  onEdit?: () => void;
  onCancel?: () => void;
  onSaveSuccess?: () => void;
  canCreate?: boolean;
  canUpdate?: boolean;
  canDelete?: boolean;
}

const CarrierDetailsPanel: React.FC<Props> = ({ carrierId, mode, onRefreshLeftPanel, onCarrierCreated, onAddNew, onEdit, onCancel, onSaveSuccess, parentCompanyId, parentDetail: parentDetailProp, canCreate = true, canUpdate = true, canDelete = true }) => {
  const appContext = useContext(AppContext);
  const modalContext = useConfirmationModalContext();
  const emptyCarrier: CarrierDetail = { id: undefined, name: '', type: '', naic: '', isActive: false, parentCompanyId: '', ibmAddress: '', carrierAddress: [], ivansSenderBoxAddresses: [] };
  const [carrier, setCarrier] = useState<CarrierDetail>(emptyCarrier);
  const [originalCarrier, setOriginalCarrier] = useState<CarrierDetail>(emptyCarrier);
  const [loading, setLoading] = useState(false);
  const [parentDetail, setParentDetail] = useState<any | null>(parentDetailProp || null);
  const [activeTab, setActiveTab] = useState(CarrierManagementConstants.TABS.CARRIER_INFORMATION.key);
  const isRootCarrier = Boolean(carrierId && carrier.id && (!carrier.parentCompanyId || carrier.id === carrier.parentCompanyId));

  useEffect(() => {
    if (carrierId) {
      const fetchCarrier = async () => {
        try
        {
        setLoading(true);
        const api = new CarrierManagementAPI();
        const data = await api.getCarrierById(carrierId);
        setCarrier(data);
        setOriginalCarrier(data);
        }
        catch (error) {
          toast.error('Error in fetch carrier detail.');
        }
        setLoading(false);
        const rightPanel = document.querySelector('.carrier-details-panel-scroll');
        if (rightPanel) rightPanel.scrollTop = 0;
        window.scrollTo({ top: 0, behavior: 'auto' });
      };
      fetchCarrier();
    } else {
      setCarrier(emptyCarrier);
      setOriginalCarrier(emptyCarrier);
    }
    setActiveTab(CarrierManagementConstants.TABS.CARRIER_INFORMATION.key);
  }, [carrierId]);

  const [parentLoading, setParentLoading] = useState(false);
  useEffect(() => {
    if (mode === 'new') {
      const newCarrier = { ...emptyCarrier, parentCompanyId: parentCompanyId || '' };
      setCarrier(newCarrier);
      setOriginalCarrier(newCarrier);
      if (parentDetailProp) {
        setParentDetail(parentDetailProp);
        setParentLoading(false);
      } else if (parentCompanyId) {
        setParentLoading(true);
        const fetchParent = async () => {
          const api = new CarrierManagementAPI();
          const parent = await api.getCarrierById(parentCompanyId);
          setParentDetail(parent);
          setParentLoading(false);
        };
        fetchParent();
      } else {
        setParentDetail(null);
        setParentLoading(false);
      }
    }
  }, [mode, parentCompanyId, parentDetailProp]);

  
  const handleChange = (field: string, value: any) => {
    if (field === 'edit' && value === true && onEdit) {
      setOriginalCarrier(carrier);
      onEdit();
    } else {
      setCarrier(prev => ({ ...prev, [field]: value }));
    }
  };

  const buildPayload = (source: CarrierDetail) => {
    const { carrierAddress, ivansSenderBoxAddresses, ...rest } = source;
    const addresses = (carrierAddress ?? []).map(addr => ({
      ...addr,
      addrDesc: addr.addrDesc || '',
      isForeign: addr.isForeign ?? false,
      isPaymentAddress: addr.isPaymentAddress ?? false,
      isFormAddress: addr.isFormAddress ?? false,
      isPrimaryAddress: addr.isPrimaryAddress ?? false,
      isPayee: addr.isPayee ?? false
    }));
    return {
      ...rest,
      addresses,
      ivansSenderBoxAddresses: ivansSenderBoxAddresses ?? []
    };
  };

  const handleSave = async () => {
    setLoading(true);
    const api = new CarrierManagementAPI();
    try {
      if (mode === 'edit') {
        await api.updateCarrier(buildPayload(carrier));
        toast.success('Carrier updated successfully');
        if (onRefreshLeftPanel) onRefreshLeftPanel();
        if (onSaveSuccess) onSaveSuccess();
      } else if (mode === 'new') {
        const payload = parentCompanyId
          ? { ...carrier, parentCompanyId }
          : { ...carrier, parentCompanyId: undefined };
        const created = await api.createCarrier(buildPayload(payload));
        toast.success('Carrier created successfully');
        if (onRefreshLeftPanel) onRefreshLeftPanel();
        if (created && created.id && onCarrierCreated) {
          onCarrierCreated(created.id, created);
        }
      }
    } catch (error) {
      toast.error('Error saving carrier');
    }
    setLoading(false);
  };

  const handleCancel = () => {
    if (mode === 'edit') {
      setCarrier(originalCarrier);
      if (onCancel) onCancel();
    } else {
      if (onCancel) onCancel();
    }
  };


  if (loading) return <Spinner loading={true} type={"SyncLoader"} />;

  return (
    <div className="card carrier-details-panel-scroll">
      <div className="d-flex align-items-center justify-content-between card-header fw-bold fs-5" style={{ background: 'white', borderBottom: '1px solid #e5e5e5' }}>
        <span>
          {mode === 'new'
            ? (!parentDetail ? CarrierManagementConstants.ADD_NEW_PARENT_COMPANY_LABEL : parentDetail.name)
            : ''}
        </span>
        <Button
          variant="primary"
          onClick={() => onAddNew && onAddNew()}
          disabled={!parentCompanyId || !canCreate}
        >
          <FontAwesomeIcon icon={'plus'} className='me-2'></FontAwesomeIcon>
          {label.ADD_NEW_WRITING ? label.ADD_NEW_WRITING : 'Add New'}
        </Button>
      </div>
      <div className="card-body p-0">
        <Tab.Container activeKey={activeTab} onSelect={async (k) => {
          if (!appContext.formSavedStatus) {
            const result = await modalContext.showConfirmation(constant.CONFIRM_EXIT);
            if (!result) return;
            appContext.formSavedStatus = true;
          }
          setActiveTab(k || CarrierManagementConstants.TABS.CARRIER_INFORMATION.key);
        }}>
          <Nav variant="tabs" className="carrier-mgmt-tabs px-3 pt-2">
            <Nav.Item>
              <Nav.Link eventKey={CarrierManagementConstants.TABS.CARRIER_INFORMATION.key}>{CarrierManagementConstants.TABS.CARRIER_INFORMATION.text}</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey={CarrierManagementConstants.TABS.CARRIER_COMMISSION.key} disabled={!carrierId}>{CarrierManagementConstants.TABS.CARRIER_COMMISSION.text}</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey={CarrierManagementConstants.TABS.COMMISSION_HISTORY.key} disabled={!carrierId}>{CarrierManagementConstants.TABS.COMMISSION_HISTORY.text}</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey={CarrierManagementConstants.TABS.RETENTION_ARRAY.key} disabled={!isRootCarrier}>{CarrierManagementConstants.TABS.RETENTION_ARRAY.text}</Nav.Link>
            </Nav.Item>
          </Nav>
          <Tab.Content>
            <Tab.Pane eventKey={CarrierManagementConstants.TABS.CARRIER_INFORMATION.key} className="carrier-tab-pane">
              {mode === 'none' ? (
                <div className="text-center text-muted py-5">Select a carrier or click '+ Add New Carrier' to begin.</div>
              ) : (
                <div style={{ position: 'relative' }}>
                  {parentLoading && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      background: 'rgba(255,255,255,0.7)',
                      zIndex: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Spinner loading={true} type={"SyncLoader"} />
                    </div>
                  )}
                  <CarrierDetailsForm
                    mode={
                      mode === 'edit'
                        ? 'edit'
                        : mode === 'view'
                        ? 'view'
                        : 'create'
                    }
                    carrierDetail={carrier}
                    onChange={handleChange}
                    onSave={handleSave}
                    onCancel={handleCancel}
                    parentCompanyName={parentDetail?.name || ''}
                    canCreate={canCreate}
                    canUpdate={canUpdate}
                    canDelete={canDelete}
                  />
                </div>
              )}
            </Tab.Pane>
            <Tab.Pane eventKey={CarrierManagementConstants.TABS.CARRIER_COMMISSION.key} className="carrier-tab-pane">
              {carrierId ? (
                <CarrierCommissionGrid
                  companyId={carrierId}
                  isActive={activeTab === CarrierManagementConstants.TABS.CARRIER_COMMISSION.key}
                  canCreate={canCreate}
                  canUpdate={canUpdate}
                  canDelete={canDelete}
                />
              ) : (
                <div className="text-center text-muted py-5">Select a carrier to view commission rates.</div>
              )}
            </Tab.Pane>
            <Tab.Pane eventKey={CarrierManagementConstants.TABS.COMMISSION_HISTORY.key} className="carrier-tab-pane">
              <div className="text-center text-muted py-5">{CarrierManagementConstants.TABS.COMMISSION_HISTORY.text}</div>
            </Tab.Pane>
            <Tab.Pane eventKey={CarrierManagementConstants.TABS.RETENTION_ARRAY.key} className="carrier-tab-pane">
              {isRootCarrier ? (
                <RetentionArrayGrid
                  companyId={carrierId}
                  parentAgency={parentCompanyId}
                  isActive={activeTab === CarrierManagementConstants.TABS.RETENTION_ARRAY.key}
                  canCreate={canCreate}
                  canUpdate={canUpdate}
                  canDelete={canDelete}
                />
              ) : (
                <div className="text-center text-muted py-5">Retention Array is available only for root carriers.</div>
              )}
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </div>
    </div>
  );
};

export default React.memo(CarrierDetailsPanel);
